import type {
  DOMConversionMap,
  DOMConversionOutput,
  SerializedTextNode,
} from 'lexical';
import { $applyNodeReplacement, $isTextNode, TextNode } from 'lexical';

type ImportDOM = (node: HTMLElement) => ReturnType<NonNullable<DOMConversionMap[string]>>;

const FONT_SIZE_TAGS = ['span', 'strong', 'em', 'i', 'code', 'mark', 'sub', 'sup'];
const PX_FONT_SIZE = /^\d+(\.\d+)?px$/;

// Wraps Lexical's default inline import, which drops an inline `font-size`, to carry only a px
// `font-size` onto the text node's style; pasted `pt` sizes (Google Docs, Word) stay dropped.
function keepFontSize(original: ImportDOM): ImportDOM {
  return (domNode) => {
    const converter = original(domNode);
    if (!converter) {
      return null;
    }
    return {
      ...converter,
      conversion: (node): DOMConversionOutput | null => {
        const output = converter.conversion(node);
        const fontSize = (node as HTMLElement).style.fontSize;
        if (!output || !PX_FONT_SIZE.test(fontSize)) {
          return output;
        }
        return {
          ...output,
          forChild: (child, parent) => {
            const result = output.forChild?.(child, parent) ?? child;
            if ($isTextNode(result)) {
              result.setStyle(`font-size: ${fontSize}`);
            }
            return result;
          },
        };
      },
    };
  };
}

export class FontSizeTextNode extends TextNode {
  static getType(): string {
    return 'font-size-text';
  }

  static clone(node: FontSizeTextNode): FontSizeTextNode {
    return new FontSizeTextNode(node.__text, node.__key);
  }

  static importJSON(serialized: SerializedTextNode): FontSizeTextNode {
    return new FontSizeTextNode(serialized.text).updateFromJSON(serialized);
  }

  // This node replaces every TextNode, but Lexical's default checks for the 'text' type, so
  // without this override empty text nodes are never removed and neighbours never merge.
  isSimpleText(): boolean {
    return this.getLatest().__mode === 0;
  }

  static importDOM(): DOMConversionMap | null {
    const base = TextNode.importDOM?.() ?? {};
    const conversions: DOMConversionMap = { ...base };
    for (const tag of FONT_SIZE_TAGS) {
      conversions[tag] = (node) =>
        keepFontSize(base[tag] ?? (() => null))(node as HTMLElement);
    }
    return conversions;
  }
}

function $createFontSizeTextNode(text = ''): FontSizeTextNode {
  return $applyNodeReplacement(new FontSizeTextNode(text));
}

export const fontSizeTextNodeReplacement = {
  replace: TextNode,
  with: (node: TextNode) => $createFontSizeTextNode(node.__text),
  withKlass: FontSizeTextNode,
};
